import lightBlue from '@material-ui/core/colors/lightBlue';
import pink from '@material-ui/core/colors/pink';
import deepPurple from '@material-ui/core/colors/deepPurple';
import indigo from '@material-ui/core/colors/indigo';
import teal from '@material-ui/core/colors/teal';
import red from '@material-ui/core/colors/red';

export const formatDate = (date) => {
    return hasTimeComponent(date) ?
    new Date(date).toLocaleDateString(
        'en',
        {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            timeZone: 'America/New_York',

        }
    ) :
    new Date(date).toLocaleDateString(
        'en',
        {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            timeZone: 'UTC',
        }
    );
}

// Formats a date as YYYY-MM-DD in Eastern Time, matching the backend's
// daily-processing date parameter format used by NLS endpoints.
export const formatNLSDate = (date) =>
    new Date(date).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

// Formats a date as YYYY-MM-DD without timezone conversion.
export const formatUSDateToISO = (date) => {
    if (date == null) {
        return '';
    }

    if (typeof date === 'string') {
        const trimmedDate = date.trim();

        // Preserve ISO date-only values as-is.
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) {
            return trimmedDate;
        }

        // Handle ISO datetime strings by taking the date portion only, avoiding timezone shifts.
        const isoDateTimeMatch = trimmedDate.match(/^(\d{4}-\d{2}-\d{2})T/);
        if (isoDateTimeMatch) {
            return isoDateTimeMatch[1];
        }

        // Convert US dates (MM/DD/YYYY) to ISO date format without shifting days.
        const usDateMatch = trimmedDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (usDateMatch) {
            const month = usDateMatch[1].padStart(2, '0');
            const day = usDateMatch[2].padStart(2, '0');
            const year = usDateMatch[3];

            return `${year}-${month}-${day}`;
        }
    }

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
        return '';
    }

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

export const capitalizeString = (str) => {
    return (typeof str === 'string' || str instanceof String) ? str.charAt(0).toUpperCase() + str.slice(1) : '';
}

export const getCurrentDateFormatted = () => {
    return formatDate(new Date());
}

export const formatTime = (date) => {
    return new Date(date).toLocaleTimeString(
        'en',
        {
            timeZone: 'America/New_York',
        }
    )
}

const hasTimeComponent = (dateString) => {
    const isoDateTimeRegex = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z?)$/;
    return isoDateTimeRegex.test(dateString);
}

export const formatDateTime = (date) => {
    return new Date(date).toLocaleDateString(
        'en',
        {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            timeZone: 'America/New_York',
        }
    )
}

export const lastFourDigits = (sensitiveString) => {
    if(sensitiveString && sensitiveString.length >= 4) {
        return '#'.repeat(sensitiveString.length - 4) + sensitiveString.slice(-4);
    }
    else {
        return '##error##';
    }
}

export const centsToDollar = (cents) => {
    return `$${(cents / 100).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
}

export const dollarsToCents = (dollars) => {
    // Handle null or undefined inputs explicitly to avoid NaN propagation
    if (dollars == null) {
        return 0;
    }

    const cleanedDollars = typeof dollars === 'string' ? dollars.replace(/,/g, '').trim() : dollars;

    // If the value is already a number, reuse it; otherwise, attempt to parse
    const parsed = typeof cleanedDollars === 'number' ? cleanedDollars : parseFloat(cleanedDollars);

    // Guard against NaN, non-finite numbers, and negative amounts
    if (!Number.isFinite(parsed) || Number.isNaN(parsed) || parsed < 0) {
        return 0;
    }

    return Math.round(parsed * 100);
}

export const aprToPercent = (apr) => {
    if (apr == null) {
        return 'N/A';
    }

    return `${parseFloat(apr).toFixed(2)}%`
}

export const sortDollarColumn = (order) => {
    return (obj1, obj2) => {
        let val1 = parseFloat(obj1.data?.replace(/[$,]+/g,""));
        let val2 = parseFloat(obj2.data?.replace(/[$,]+/g,""));

        return (val1 - val2) * (order === 'asc' ? 1 : -1);
    };
}

export const formatCreditScore = (creditScorePB, creditScoreCB) => {
    if (creditScorePB && creditScoreCB) {
        return `${creditScorePB}, ${creditScoreCB}`
    }
    else {
        return creditScorePB
    }
}

export const formatCodes = (codes, isAdmin) => {
    if(codes) {
        let codeArray = codes.split(", ");
        codeArray.forEach(function(code, i) {
            if (code === 'PIF') codeArray[i] = 'PAID IN FULL';
            else if (code === 'INCR') codeArray[i] = 'LOAN INCREASE';
            else if (code === 'DECR') codeArray[i] = 'LOAN DECREASE';
            else if (code === 'PIC') codeArray[i] = 'CANCEL';
        });

        const showP4 = ['CURRENT', 'AUTO PAY', 'PAID IN FULL', 'LOW BALANCE', 'SPANISH', 'NOCALL', 'NOCOMM', 'CLAIM FILED', 'NO BILLING', 'LOAN INCREASE', 'LOAN DECREASE', 'LAM', 'BANKRUPTCY', 'BORROWER DECEASED', 'LAM COMPLETE', 'CANCEL', 'DO NOT REPORT', 'CBR DELETE'];
        const showAdmin = [];
        return codeArray.filter(x => showP4.includes(x)).concat((isAdmin ? codeArray.filter(x => showAdmin.includes(x)) : []));
    }
    return [];
}

export const dealerServerSidePaginationSearchParams = (page, perPage, searchText, filterList) => {
    const filterObject = buildDealerFilterObject(searchText, filterList);
    return `?perPage=${perPage}&page=${page}` +
        `${filterObject && filterObject !== '{}' ? `&q=${encodeURIComponent(filterObject)}` : ''}`;
}

export const loanServerSidePaginationSearchParams = (page, perPage, searchText, filterList) => {
    const filterObject = buildLoanFilterObject(searchText, filterList);
    return `?perPage=${perPage}&page=${page}` +
        `${filterObject && filterObject !== '{}' ? `&q=${encodeURIComponent(filterObject)}` : ''}`;
}

export const promoToColor = (promo) => {
    let color;

    switch(promo) {
        case 'BalPay':
            color = pink[400];
            break;
        case 'OHNO':
            color = lightBlue[400];
            break;
        case 'StipPay':
            color = deepPurple[400];
            break;
        case 'APR Buy Down':
            color = indigo[400];
            break;
        case 'Staged Funding':
            color = teal[400];
            break;
        case 'POP':
            color = red[400];
            break;
        case 'Power360':
            color = indigo[200];
            break;
        default:
            color = teal[400];
            break;
    }

    return color;
}

export const promosToChips = (loan) => {
    return [
        ...(loan.hasStagedFunding ? ['Staged Funding'] : []),
        ...(loan.hasBuyDown ? ['APR Buy Down'] : []),
        ...(loan.hasStipPay ? ['StipPay'] : []),
        ...(loan.hasBalPay ? ['BalPay'] : []),
        ...(loan.hasOhno ? ['OHNO'] : []),
        ...(loan.hasPop ? ['POP'] : []),
        ...(loan.hasPower360 ? ['Power360'] : []),
    ]
}

const buildDealerFilterObject = (searchText, filterList) => {
    const searchObject = searchText? {
        business_name_or_legal_business_name_or_dealer_number_or_organization_i_cont: searchText
    } : {};
    const finalFilterObject = { ...searchObject, ...dealersFilters2SearchParam(filterList) }

    return JSON.stringify(finalFilterObject);
};

const dealersFilters2SearchParam = (filterList) => {
    if (!filterList) {
        return {};
    }

    const indexes = {
        needs_review_eq: 5,
        is_concierge_eq: 19,
        test_trans_status_eq: 29,
        has_fincen_id_eq: 30,
    }


    let filterSearchParams = {};

    for(let i = 0; i < filterList.length; i++) {
        const filter = filterList[i];

        // If index matches an indexes value, add its key and filter to search params
        if (Object.values(indexes).includes(i)) {
            const key = Object.keys(indexes).find(key => indexes[key] === i);
            if (filter.length > 0) {
                filterSearchParams[key] = filter[0];
            }
        }
    }

    return filterSearchParams;
}

const buildLoanFilterObject = (searchText, filterList) => {
    const searchObject = searchText? {
        loan_number_or_dealer_dealer_number_i_cont: searchText
    } : {};
    const finalFilterObject = { ...searchObject, ...loanFilter2IndexedSearchParams(filterList) }

    return JSON.stringify(finalFilterObject);
}

const loanFilter2IndexedSearchParams = (filterList) => {
    if (!filterList) {
        return {};
    }

    const indexes = {
        loan_status_i_cont_any: 1,
        apr_eq: 4,
        term_months_eq: 5,
        dealer_legal_business_name_eq: 8,
        dealer_organization_eq: 10,
        lender_long_name_eq: 11,
        dealer_dealer_number_eq: 15,
        promoFilters: 17, // Specific logic for has_bal_pay, has_ohno, etc.
        dealer_is_concierge_eq: 20,
        is_delinquent_eq: 21,
        is_held_eq: 22,
        has_staged_funding_eq: 23,
        origin_eq: 24,
        loan_type_eq: 25,
        loan_status_code_i_cont: 27,
        portfolio_code_eq: 29,
    }

    let filterSearchParams = {};

    for(let i = 0; i < filterList.length; i++) {
        const filter = filterList[i];

        // If index matches an indexes value, add its key and filter to search params
        if (Object.values(indexes).includes(i)) {
            const key = Object.keys(indexes).find(key => indexes[key] === i);

            if (key === 'promoFilters') {
                const promoFilterList = parsePromoFilter(filter);
                for (let j = 0; j < promoFilterList.length; j++) {
                    filterSearchParams = { ...filterSearchParams, ...promoFilterList[j] };
                }
                continue;
            }

            if (filter.length > 1) {
                filterSearchParams[key] = filter;
            } else if (filter.length === 1) {
                filterSearchParams[key] = filter[0];
            }
        }
    }

    return filterSearchParams;
}

const parsePromoFilter = (filter) => {
    let promoFilters = [];

    const hasPop = filter.includes('POP');
    const hasOhno = filter.includes('OHNO');
    const hasBalPay = filter.includes('BalPay');
    const hasStipPay = filter.includes('StipPay');
    const hasPower360 = filter.includes('Power360');
    const hasAprBuyDown = filter.includes('APR Buy Down');
    const hasStagedFunding = filter.includes('Staged Funding');

    if (hasPop) promoFilters.push({ has_pop_eq: true });
    if (hasOhno) promoFilters.push({ has_ohno_eq: true });
    if (hasBalPay) promoFilters.push({ has_bal_pay_eq: true });
    if (hasStipPay) promoFilters.push({ has_stip_pay_eq: true });
    if (hasPower360) promoFilters.push({ has_power360_eq: true });
    if (hasAprBuyDown) promoFilters.push({ has_buy_down_eq: true });
    if (hasStagedFunding) promoFilters.push({ has_staged_funding_eq: true });

    return promoFilters
}

/* TIME AGO */
const epochs = [
    ['year', 31536000],
    ['month', 2592000],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
    ['second', 1]
];

const getDuration = (timeAgoInSeconds) => {
    for (let [name, seconds] of epochs) {
        const interval = Math.floor(timeAgoInSeconds / seconds);
        if (interval >= 1) {
            return {
                interval: interval,
                epoch: name
            };
        }
    }
};

export const timeAgo = (date) => {
    const timeAgoInSeconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (timeAgoInSeconds > 0) {
        const {interval, epoch} = getDuration(timeAgoInSeconds);
        const suffix = interval === 1 ? '' : 's';
        return `${interval} ${epoch}${suffix} ago`;
    } else {
        return 'now'
    }
};

/* END TIME AGO */

export const formatCountdown = (seconds) => {
    let minutes = Math.floor(seconds / 60);
    let secondsLeft = seconds % 60;
    let secondsFormatted = secondsLeft < 10 ? `0` + secondsLeft : secondsLeft
    return `${minutes}:${secondsFormatted}`
};

export const parseCentsToDisplay = (cents) => {
    return parseInt(cents) ? centsToDollar(cents) : '$0';
}

export const stage2Text = (stage) => {
    let stageText = '';
    switch (stage) {
        case "unstaged":
            stageText = 'Unstaged';
            break;
        case "first":
            stageText = 'First stage';
            break;
        case 'second':
            stageText = 'Second stage';
            break;
        case 'third':
            stageText = 'Third stage';
            break;
        case 'fourth':
            stageText = 'Fourth stage';
            break;
        case 'final':
            stageText = 'Final stage';
            break;
        case 'none':
            stageText = "No stage";
            break;
        default:
            stageText = 'N/A';
            break;
    }

    return stageText;
}

export const stageSort = (arr, sortDisplayValues = false) => arr.sort(function(a, b) {
    const sortOrder = sortDisplayValues
        ? ['unstaged', 'first', 'second', 'third', 'fourth', 'final']
        : ['Unstaged', 'First stage', 'Second stage', 'Third stage', 'Fourth stage', 'Final stage'];
    return sortOrder.indexOf(a) - sortOrder.indexOf(b);
});

export const humanizeSnakeCaseString = (str) => {
    if (!str) return '';
    if (typeof str !== 'string') return '';

    return str.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

export const emailStringArrayToArray = (emails) => {
    try {
        return JSON.parse(emails);
    } catch (e) {
        return [];
    }
};
